import { lazy, Suspense, useState } from "react";
import { Loader2, Wrench } from "lucide-react";
import type { CdnProduct } from "@/lib/cdnProducts";

// The heavy panel (chat state, streaming, markdown) is code-split so it
// only downloads after the user clicks the pulsing button.
const FitmentPanel = lazy(() => import("./FitmentPanel"));

interface Props {
  product: CdnProduct;
}

/**
 * Tiny static entry point rendered inside the fitment section on the
 * product-detail page. Zero AI/chat code enters the initial bundle —
 * clicking the button lazy-loads the FitmentPanel chunk.
 */
export default function FitmentPulseButton({ product }: Props) {
  const [open, setOpen] = useState(false);

  // Suppress the widget when the product has no fitment-relevant metadata.
  const mpn =
    (product['manufacturer_part_no'] as string | null | undefined) ||
    (product['admin_mpn'] as string | null | undefined) ||
    "";
  const hasContext = !!mpn || !!product.VendorName || !!product.PartTypeName;
  if (!hasContext) return null;

  if (open) {
    return (
      <div className="mt-6 rounded-lg border border-primary/40 bg-card overflow-hidden">
        <Suspense
          fallback={
            <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading fitment assistant…
            </div>
          }
        >
          <FitmentPanel product={product} onClose={() => setOpen(false)} />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <div className="relative inline-block">
        <span
          className="absolute inset-0 rounded-full bg-primary/40 animate-ping"
          aria-hidden="true"
        />
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="relative inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 hover:bg-primary/90 focus:outline-hidden focus:ring-2 focus:ring-primary/50 transition-colors"
        >
          <Wrench className="w-4 h-4" />
          Check if this fits my vehicle
        </button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground max-w-md">
        Ask our AI parts specialist — powered by manufacturer specs. Always verify fitment before installing.
      </p>
    </div>
  );
}